import { get } from 'svelte/store';
//Stores
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
//Configs
import { devices_config } from '@devices/index';
//Helpers
import { getSwitchPeripheralDeviceByParentID, isPeripheralDevice } from './addons/addon_device_helpers';
import { is_GBLE, isProThreeEM, real_id, isCctOnlyDevice, isXMODDevice } from './interface_helpers';
import { supportsColorModeChange } from './night_mode_helpers';
import { isVirtualDevice } from './virtual_helpers/virtual_components_helpers';
import { isIRControllerCategory } from './ir_controller_helpers';
import { isXT1ComponentDevice, isXT1Module } from './xt1_helpers/template_helpers';
import {
	getXT1AppGroupTypes,
	isXT1GroupSupported,
	type GroupType,
} from './xt1_helpers/template_group_helpers';
import { isTestUser } from './user_helpers';
import { HiddenDevicesRoomID } from './service_rooms_helpers';
//Types
import type { Device } from 'src/types/Device';
import type { Group } from 'src/types/Group';

export const defaultGroupObject: Group = {
	name: undefined,
	image: 'images/room_def/bedroom_img_def_m.jpg',
	type: undefined,
	devices: [],
	room_id: -1,
	position: 0,
};

export const isProEMGroupSupported = (device: Device): boolean => {
	return isPeripheralDevice(device?.type) || device?.category == 'emeter';
};

export const proEMGroupConditions = (device: Device): boolean => {
	if (isPeripheralDevice(device?.type)) {
		// The Pro EM50 has a built-in relay and an Add-on switch peripheral,
		// which appear as separate devices in the Add Group wizard.
		return /^(SPEM-002CEBEU50)$/.test(get(devices)[real_id(device.id)]?.type);
	}
	if (device?.category == 'emeter' && device?.channel === 0) {
		return (
			/^(SPEM-002CEBEU50|S3EM-002CXCEU|S4EM-002CXCEU|SHEM|SHEM-3)$/.test(device.type) || // Emeter with a built-in relay
			devices_settings.getByRealId(device.id)?.['switch:100'] //Emeter with a Add-on switch peripheral
		);
	}
	return false;
};

/**
 * @param {Device} device - The main device to check.
 * @returns {Device} - The extracted peripheral device (if exists) or the original device.
 */
export const getDeviceInGroup = (device: Device): Device => {
	if (isProThreeEM(device?.type)) {
		// Attempt to retrieve the switch peripheral associated with the main device.
		const peripheralDevice = getSwitchPeripheralDeviceByParentID(device.id);
		if (peripheralDevice) {
			// If a peripheral device exists, return it with the main device ID.
			return { ...peripheralDevice, id: real_id(peripheralDevice.id) };
		}
	}
	return device;
};

/**
 * Helper to check if a device is supported in groups
 * @param device
 */
export const isDeviceSupportedInGroup = (device: Device): boolean => {
	if (
		device.shared ||
		is_GBLE(device) ||
		isVirtualDevice(device.type) ||
		device.category === 'sensor' ||
		device.category === 'gateway' ||
		isIRControllerCategory(device) ||
		device.category === 'power_meter'
	) {
		return false;
	}

	if (
		(isXMODDevice(device?.type) || isXT1ComponentDevice(device)) &&
		device.category === 'inputs_reader'
	) {
		return false;
	}

	if (isXT1Module(device.type)) {
		return (
			device?.category === 'thermostat' ||
			isXT1ComponentDevice(device) ||
			getXT1AppGroupTypes(device).length > 0
		);
	}

	return true;
};

/**
 * Helper for the filter of devices based on the group type
 * @param device
 * @param groupType
 */
export const isGroupSupported = (device: Device, groupType: string): boolean => {
	if (!isDeviceSupportedInGroup(device)) {
		return false;
	}

	if (groupType === 'relay' && isProEMGroupSupported(device)) {
		return proEMGroupConditions(device);
	}

	if (isXMODDevice(device?.type) || isXT1ComponentDevice(device)) {
		const isLightMode =
			['light', 'cct', 'rgb', 'rgbw', 'rgbcct'].includes(device?.mode) ||
			device?.category === 'light';
		const isSwitchMode =
			['relay', 'switch', 'output_reader'].includes(device?.mode) ||
			device?.category === 'relay' ||
			device?.category === 'switch';
		if (groupType === 'light' && isLightMode) return true;
		if (groupType === 'relay' && isSwitchMode) return true;
		if (device?.category === 'module') {
			if (
				groupType === 'light' &&
				['light', 'cct', 'rgb', 'rgbw', 'rgbcct'].includes(device?.mode)
			)
				return true;
			if (groupType === 'relay' && ['relay', 'switch'].includes(device?.mode)) return true;
		}
		return false;
	}

	if (isXT1Module(device?.type)) {
		return isXT1GroupSupported(device, groupType as GroupType);
	}

	return (
		(isTestUser() || device.room_id !== HiddenDevicesRoomID) &&
		(device.category === groupType ||
			(groupType === 'light' && device.category === 'relay') ||
			(groupType === 'light' && device.category === 'module' && device.mode === 'cct') ||
			(groupType === 'light' && isCctOnlyDevice(device.type)) ||
			(groupType === 'relay' && (device?.mode === 'output_reader' || device?.mode === 'relay')) ||
			(groupType === 'relay' && device.category === 'breaker'))
	);
};

/**
 * Whether a bulb group is eligible for an in-place Color/White mode switch.
 * Eligible only when EVERY device in the group supports mode change without
 * a firmware swap or a profile change (i.e. passes `supportsColorModeChange`,
 * which already excludes RGBW2, Duo, profile devices, CCT-only devices, etc.).
 */
export const isGroupModeChangeEligible = (lightDevices: Device[]): boolean => {
	if (!lightDevices?.length) return false;
	return lightDevices.every((d) => supportsColorModeChange(d?.type));
};

/**
 * Returns the friendly name (or raw type) of the first device in the group
 * that prevents a group-level Color/White mode switch, or `null` when every
 * device is eligible. Used to render a typed warning in place of the toggle.
 */
export const getModeChangeBlockingType = (lightDevices: Device[]): string | null => {
	if (!lightDevices?.length) return null;
	const blocking = lightDevices.find((d) => !supportsColorModeChange(d?.type));
	if (!blocking) return null;
	return devices_config[blocking.type]?.friendly_name || blocking.type;
};
